/**
 * Pure data and string utilities.
 * For browser-specific helpers see browserUtils.js.
 * For UI/visual constants see uiConstants.js.
 */

export const encodeQueryData = (data) => {
    const ret = [];
    for (let d in data) {
        ret.push(encodeURIComponent(d) + "=" + encodeURIComponent(data[d]));
    }
    return ret?.join("&");
};

export const deepClean = (input) => {
    if (Array.isArray(input)) {
        const cleanedArray = input
            .map(deepClean)
            .filter(
                (item) =>
                    item !== undefined &&
                    item !== null &&
                    item !== "" &&
                    !(typeof item === "object" && Object.keys(item).length === 0)
            );

        return cleanedArray.length ? cleanedArray : undefined;
    }

    if (typeof input === "object" && input !== null) {
        const cleanedObject = Object.entries(input).reduce((acc, [key, value]) => {
            const cleanedValue = deepClean(value);

            if (
                cleanedValue !== undefined &&
                cleanedValue !== null &&
                cleanedValue !== "" &&
                !(typeof cleanedValue === "object" &&
                    Object.keys(cleanedValue).length === 0)
            ) {
                acc[key] = cleanedValue;
            }

            return acc;
        }, {});

        return Object.keys(cleanedObject).length ? cleanedObject : undefined;
    }

    return input;
};

export const buildFormData = (data = {}) => {
    const formData = new FormData();
    const isBinary = (value) => value instanceof File || value instanceof Blob;

    const toBinary = (value) => {
        if (!value) return null;
        if (isBinary(value?.originFileObj)) return value.originFileObj;
        if (isBinary(value?.file?.originFileObj)) return value.file.originFileObj;
        if (isBinary(value?.file)) return value.file;
        if (isBinary(value)) return value;
        return null;
    };

    const append = (key, value) => {
        if (value === undefined || value === null) return;

        const binary = toBinary(value);
        if (binary) {
            formData.append(key, binary);
            return;
        }

        if (Array.isArray(value?.fileList)) {
            value.fileList.forEach((f) => {
                const fileBinary = toBinary(f);
                if (fileBinary) formData.append(`${key}[]`, fileBinary);
            });
            return;
        }

        if (Array.isArray(value)) {
            value.forEach((v) => append(`${key}[]`, v));
            return;
        }

        if (typeof value === "boolean") {
            formData.append(key, value ? "1" : "0");
            return;
        }

        if (typeof value === "object") {
            formData.append(key, JSON.stringify(value));
            return;
        }

        formData.append(key, value);
    };

    Object.entries(data).forEach(([k, v]) => append(k, v));

    return formData;
};

export const toTitleCase = (text = "") =>
    text
        ?.toLowerCase()
        ?.split(" ")
        ?.map((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1))
        ?.join(" ");

export const sanitizeTextAndNumber = (value) => value.replace(/[^a-zA-Z0-9]/g, "");
export const allowTextNumberWithSpace = (value) => /^[a-zA-Z0-9 ]*$/.test(value);
export const sanitizeText = (value) => {
    const text = String(value ?? "");
    return `${[...text]
        .filter((char) => /[a-zA-Z0-9]/.test(char))
        .join("")
    }`;
};

// Re-exports for backwards compatibility — prefer importing directly from their modules.
export { pinSizeOptions, TYPE_STYLE } from "./uiConstants";
export { isPageReload, isLocalhost, generateRandomIP, urlIsMediaFile, getZoomByRank } from "./browserUtils";

// utils/getElementMetrics.js
export const getElementMetrics = (selector) => {
    let element = null;

    if (typeof selector === "string") {
        element = document.querySelector(selector);
    } else if (selector instanceof HTMLElement) {
        element = selector;
    }

    if (!element) {
        console.warn("Element not found");
        return null;
    }

    const rect = element.getBoundingClientRect();

    return {
        width: rect.width,
        height: rect.height,
        top: rect.top,
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom,
        x: rect.x,
        y: rect.y
    };
};